import type { PurchaseRequisition } from '../types/purchasing';
import { purchaseListStatus } from './purchasingStatus';
import { formatCostObjectLabel } from './purchasingSpendClass';

export type PurchaseListSortKey = 'pr' | 'items' | 'amount' | 'cost' | 'gate' | 'status' | 'po';
export type PurchaseListSortDir = 'asc' | 'desc';

export function parsePurchasingDocSeq(value?: string): number {
  const match = String(value || '')
    .trim()
    .match(/^(?:PR|PO)-(\d{4})-(\d+)$/i);
  if (!match) return 0;
  return Number(match[1]) * 100000 + Number(match[2]);
}

function listAmount(row: PurchaseRequisition): number {
  if (row.status === 'purchased' && Number.isFinite(row.purchasedAmount)) return row.purchasedAmount as number;
  return Number.isFinite(row.amount) ? row.amount : 0;
}

function gateLabel(row: PurchaseRequisition): string {
  if (row.status === 'approved' && row.purchaseVariance) {
    return `Actual · ${row.purchaseVariance.reviewerName || ''}`;
  }
  if (row.status === 'approved' || row.status === 'purchased') return '';
  const pending = row.approvalSteps.find(s => s.status === 'pending');
  return pending ? `${pending.roleLabel} · ${pending.userName}` : '';
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export function comparePurchaseListRows(
  a: PurchaseRequisition,
  b: PurchaseRequisition,
  key: PurchaseListSortKey,
  dir: PurchaseListSortDir,
): number {
  let n = 0;
  if (key === 'pr') n = parsePurchasingDocSeq(a.prNumber) - parsePurchasingDocSeq(b.prNumber);
  else if (key === 'po') {
    n = (parsePurchasingDocSeq(a.poNumber) || parsePurchasingDocSeq(a.prNumber))
      - (parsePurchasingDocSeq(b.poNumber) || parsePurchasingDocSeq(b.prNumber));
  } else if (key === 'amount') n = listAmount(a) - listAmount(b);
  else if (key === 'items') n = compareText(a.title || '', b.title || '');
  else if (key === 'cost') n = compareText(formatCostObjectLabel(a.costObject), formatCostObjectLabel(b.costObject));
  else if (key === 'gate') n = compareText(gateLabel(a), gateLabel(b));
  else if (key === 'status') n = compareText(purchaseListStatus(a).label, purchaseListStatus(b).label);
  if (n === 0) {
    n = (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0);
  }
  return dir === 'asc' ? n : -n;
}

export function sortPurchaseListRows(
  rows: PurchaseRequisition[],
  key: PurchaseListSortKey,
  dir: PurchaseListSortDir,
): PurchaseRequisition[] {
  return [...rows].sort((a, b) => comparePurchaseListRows(a, b, key, dir));
}
